/**
 * Typed client for the LockerEDU API.
 * Reads JWT from localStorage; all calls hit api.lockeredu.com (or workers.dev fallback).
 */

const API_BASE =
  import.meta.env.VITE_API_BASE ?? "https://lockeredu-api.mvirga01.workers.dev";
const TOKEN_KEY = "lockeredu.jwt";
const USER_KEY = "lockeredu.user";

export interface SignedInUser {
  userId: string;
  role: "STUDENT" | "TEACHER" | "PARENT" | "ADMIN" | "SUB";
  schoolId: string;
  displayName: string;
  email: string;
}

export type KioskHealth =
  | "enforcing"
  | "stale"
  | "disabled"
  | "different_device" // Day 10 — heartbeating device ≠ check_in device
  | "unknown";

export interface RosterRow {
  studentId: string;
  displayName: string;
  state:
    | "NOT_CHECKED_IN"
    | "LOCKED"
    | "UNLOCKED_AUTO"
    | "UNLOCKED_TEACHER"
    | "VIOLATION"
    | "OFFLINE";
  checkedInAt: string | null;
  unlockReason: string | null;
  violationCount: number;
  /** Day 8.3 — heartbeat-derived OS-enforcement signal. */
  kioskHealth: KioskHealth;
  lastKioskHealthAt: number | null;
}

export interface RoomStatus {
  room: { roomId: string; name: string };
  session: {
    sessionId: string;
    startTime: string;
    endTime: string;
    status: "ACTIVE" | "ENDED" | "SCHEDULED" | "CANCELLED";
    currentMode: string | null;
  } | null;
  roster: RosterRow[];
  counts: Record<string, number>;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function getUser(): SignedInUser | null {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? (JSON.parse(raw) as SignedInUser) : null;
}
export function setSession(token: string, user: SignedInUser): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}
export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  const tok = getToken();
  if (tok) headers.set("authorization", `Bearer ${tok}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    clearSession();
    throw new Error("unauthorized");
  }
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(body.error ?? `http_${res.status}`);
  return body as T;
}

export const api = {
  authRequest: (email: string) =>
    request<{ ok: true }>("/auth/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  authVerify: (email: string, code: string) =>
    request<{ token: string; user: SignedInUser }>("/auth/verify", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    }),

  /** Day 9 — exchange a Google ID token for a LockerEDU JWT. */
  authGoogle: (idToken: string) =>
    request<{
      token: string;
      user: SignedInUser;
      mergePath: "google_sub_match" | "email_merge" | "new_user";
    }>("/auth/google", {
      method: "POST",
      body: JSON.stringify({ idToken }),
    }),

  startSession: (roomId: string, durationMinutes = 40) =>
    request<{ session: { sessionId: string }; alreadyActive?: boolean }>(
      "/lock/sessions/start",
      { method: "POST", body: JSON.stringify({ roomId, durationMinutes }) },
    ),

  endSession: (sessionId: string) =>
    request<{ ok: true; status: "ENDED" }>("/unlock/sessions/end", {
      method: "POST",
      body: JSON.stringify({ sessionId }),
    }),

  emergencyUnlock: (sessionId: string, studentId: string, reason?: string) =>
    request<{ ok: true; state: "UNLOCKED_TEACHER" }>("/unlock/teacher-key", {
      method: "POST",
      body: JSON.stringify({ sessionId, studentId, reason }),
    }),

  /** Demo helper: as TEACHER role, simulate a student tapping in. */
  simulateTapIn: (roomId: string, studentId: string) =>
    request<{ ok: true; state: "LOCKED" }>("/lock/tap-in", {
      method: "POST",
      body: JSON.stringify({ roomId, studentId }),
    }),

  roomStatus: (roomId: string) => request<RoomStatus>(`/rooms/${roomId}/status`),

  /** ADMIN only. Wipes sessions/check_ins/events for the school. Demo helper. */
  resetDemo: () =>
    request<{ ok: true; schoolId: string }>("/admin/demo/reset", {
      method: "POST",
    }),
};

export const DEMO_ROOM_ID = "room_2209";
